import React from 'react';
import HelpWalkthrough from '../../shared/HelpWalkthrough';
import '../helpStages.css';

// "How journeys work" walkthrough with animated demo stages, matching the
// Campaigns and Communications walkthroughs.

const Tick = ({ size = 9 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polyline points="20 6 9 17 4 12" /></svg>
);

const MailIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
);
const ChatIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
);

/* 1 ── a journey assembling itself: trigger, steps, goal */
const TimelineStage = () => (
  <div className="hs-float" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
    <span className="hs-pop" style={{ width: 12, height: 12, borderRadius: '50%', background: '#29282D', flexShrink: 0, animationDelay: '0.1s' }} />
    <span className="hs-rise" style={{ width: 16, height: 2, background: '#D6D6DC', animationDelay: '0.25s' }} />
    <span className="hs-pop" style={{ width: 30, height: 30, borderRadius: 9, background: '#fff', color: '#007EA0', boxShadow: '0 6px 16px rgba(26,26,30,0.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', animationDelay: '0.35s' }}><MailIcon /></span>
    <span className="hs-rise" style={{ width: 16, height: 2, background: '#D6D6DC', animationDelay: '0.5s' }} />
    <span className="hs-pop hs-chip" style={{ background: '#fff', color: '#8E8E93', boxShadow: '0 6px 16px rgba(26,26,30,0.1)', animationDelay: '0.6s' }}>14 days</span>
    <span className="hs-rise" style={{ width: 16, height: 2, background: '#D6D6DC', animationDelay: '0.75s' }} />
    <span className="hs-pop" style={{ width: 30, height: 30, borderRadius: 9, background: '#fff', color: '#007EA0', boxShadow: '0 6px 16px rgba(26,26,30,0.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', animationDelay: '0.85s' }}><ChatIcon /></span>
    <span className="hs-rise" style={{ width: 16, height: 2, background: '#D6D6DC', animationDelay: '1.0s' }} />
    <span className="hs-pop" style={{ width: 26, height: 26, borderRadius: '50%', background: '#007EA0', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', animationDelay: '1.1s' }}><Tick size={11} /></span>
  </div>
);

/* 2 ── the goal: a client books and exits before the nudge */
const GoalStage = () => (
  <div className="hs-float" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
    <div className="hs-panel hs-rise" style={{ width: 200, animationDelay: '0.1s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
        <span className="hs-avatar" style={{ background: '#C2557A', opacity: 0.85 }} />
        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#1A1A1E' }}>Sophie books a balayage</span>
      </div>
    </div>
    <span className="hs-pop hs-chip" style={{ background: 'rgba(0,126,160,0.1)', color: '#007EA0', animationDelay: '0.7s' }}>
      <Tick size={8} /> Journey complete, nudge skipped
    </span>
  </div>
);

/* 3 ── recipes */
const RecipesStage = () => (
  <div className="hs-float" style={{ display: 'flex', gap: 8 }}>
    {[
      { label: 'Welcome', d: 0.1 },
      { label: 'Win back', d: 0.25 },
      { label: 'Birthday', d: 0.4 },
    ].map((r) => (
      <span key={r.label} className="hs-rise" style={{
        background: '#fff', borderRadius: 12, boxShadow: '0 8px 20px rgba(26,26,30,0.1)',
        padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#1A1A1E', animationDelay: `${r.d}s`
      }}>
        {r.label}
      </span>
    ))}
  </div>
);

/* 4 ── guardrails */
const GuardrailsStage = () => (
  <div className="hs-float" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
    {[
      { label: 'Opted-in clients only', d: 0.1 },
      { label: 'Texts wait for daytime hours', d: 0.3 },
      { label: 'Unsubscribe exits instantly', d: 0.5 },
    ].map((g) => (
      <span key={g.label} className="hs-rise hs-chip" style={{ background: '#fff', color: '#4B4B52', boxShadow: '0 6px 16px rgba(26,26,30,0.08)', padding: '7px 12px', fontSize: 11, animationDelay: `${g.d}s` }}>
        <span style={{ color: '#007EA0', display: 'inline-flex' }}><Tick size={9} /></span> {g.label}
      </span>
    ))}
  </div>
);

const SLIDES = [
  {
    key: 'timeline',
    title: 'Set it up once, it follows every client',
    body: 'A journey is a sequence: a trigger, then messages and waits in order. New client joins, welcome email goes out, two weeks pass, a friendly text follows. You build it in minutes on a simple timeline.',
    Stage: TimelineStage,
  },
  {
    key: 'goal',
    title: 'It knows when to stop',
    body: 'Every journey can end early the moment the client books. Nobody gets nudged about something they have already done, which keeps your messages welcome.',
    Stage: GoalStage,
  },
  {
    key: 'recipes',
    title: 'Start from a proven recipe',
    body: 'Welcome series, rebook nudges, win-backs, birthdays and more, each with timing and copy already in place. Adjust the words, switch it on, done.',
    Stage: RecipesStage,
  },
  {
    key: 'guardrails',
    title: 'Polite by design',
    body: 'Journeys only message opted-in clients, texts queue for daytime hours, and unsubscribing removes someone instantly. The rules are built in so you never have to think about them.',
    Stage: GuardrailsStage,
  },
];

const JourneysHelp = ({ onClose }) => (
  <HelpWalkthrough eyebrow="How journeys work" slides={SLIDES} onClose={onClose} />
);

export default JourneysHelp;
